import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, ShieldAlert, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";

interface Props {
  clientEmail: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

type Brand = { id: string; name: string; logo_url: string | null };
type Pref = { brand_id: string; status: string };
type Suppression = { brand_id: string | null; scope: "global" | "brand"; reason: string };

const SUPPRESSION_LABELS: Record<string, string> = {
  hard_bounce: "Hard bounce",
  spam_complaint: "Spam complaint",
  manual: "Manual",
  unsubscribe: "Unsubscribed",
  invalid: "Invalid address",
};

export const ClientSubscriptionsPanel = ({ clientEmail, firstName, lastName }: Props) => {
  const qc = useQueryClient();
  const email = clientEmail?.trim().toLowerCase() || null;
  const queryKey = ["client-subscriptions", email];

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!email,
    queryFn: async () => {
      const [brandsRes, subRes, supRes] = await Promise.all([
        supabase.from("business_brands").select("id, name, logo_url").order("name"),
        supabase.from("email_subscribers").select("id, email").ilike("email", email!).maybeSingle(),
        supabase.from("email_suppressions").select("brand_id, scope, reason").ilike("email", email!),
      ]);
      if (brandsRes.error) throw brandsRes.error;
      if (supRes.error) throw supRes.error;

      let prefs: Pref[] = [];
      let subscriberId: string | null = subRes.data?.id ?? null;
      if (subscriberId) {
        const { data: p, error } = await supabase
          .from("email_subscriber_brand_prefs")
          .select("brand_id, status")
          .eq("subscriber_id", subscriberId);
        if (error) throw error;
        prefs = (p ?? []) as Pref[];
      }
      return {
        brands: (brandsRes.data ?? []) as Brand[],
        subscriberId,
        prefs,
        suppressions: (supRes.data ?? []) as Suppression[],
      };
    },
  });

  // Local optimistic overrides: brand_id -> status
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  useEffect(() => {
    setOverrides({});
  }, [email]);

  if (!email) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        Add a client email to manage subscriptions.
      </div>
    );
  }

  const ensureSubscriber = async (): Promise<string> => {
    if (data?.subscriberId) return data.subscriberId;
    // Insert; on conflict, re-select
    const { data: ins, error } = await supabase
      .from("email_subscribers")
      .insert({
        email,
        first_name: firstName || null,
        last_name: lastName || null,
      })
      .select("id")
      .single();
    if (!error && ins) return ins.id;
    const { data: existing, error: selErr } = await supabase
      .from("email_subscribers")
      .select("id")
      .ilike("email", email)
      .single();
    if (selErr) throw selErr;
    return existing.id;
  };

  const handleToggle = async (brandId: string, nextOn: boolean) => {
    const previous = overrides[brandId] ?? data?.prefs.find((p) => p.brand_id === brandId)?.status ?? "none";
    setOverrides((o) => ({ ...o, [brandId]: nextOn ? "subscribed" : "unsubscribed" }));
    setPending((p) => ({ ...p, [brandId]: true }));
    try {
      const subscriberId = await ensureSubscriber();
      if (nextOn) {
        const { error } = await supabase
          .from("email_subscriber_brand_prefs")
          .upsert(
            {
              subscriber_id: subscriberId,
              brand_id: brandId,
              status: "subscribed",
              source: "manual",
              consent_at: new Date().toISOString(),
              unsubscribed_at: null,
              unsubscribed_reason: null,
            },
            { onConflict: "subscriber_id,brand_id" },
          );
        if (error) throw error;
      } else {
        // Only update if a row exists; otherwise no-op
        const existing = data?.prefs.find((p) => p.brand_id === brandId);
        if (existing) {
          const { error } = await supabase
            .from("email_subscriber_brand_prefs")
            .update({
              status: "unsubscribed",
              unsubscribed_at: new Date().toISOString(),
              unsubscribed_reason: "Manual via CRM client card",
            })
            .eq("subscriber_id", subscriberId)
            .eq("brand_id", brandId);
          if (error) throw error;
        }
      }
      await qc.invalidateQueries({ queryKey });
      setOverrides((o) => {
        const { [brandId]: _, ...rest } = o;
        return rest;
      });
    } catch (err: any) {
      setOverrides((o) => ({ ...o, [brandId]: previous }));
      toast({
        title: "Could not update subscription",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setPending((p) => {
        const { [brandId]: _, ...rest } = p;
        return rest;
      });
    }
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Email Subscriptions</h3>
      </div>
      <div className="divide-y">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          data?.brands.map((brand) => {
            const globalSup = data.suppressions.find((s) => s.scope === "global");
            const brandSup = data.suppressions.find(
              (s) => s.scope === "brand" && s.brand_id === brand.id,
            );
            const suppression = globalSup || brandSup;
            const pref = data.prefs.find((p) => p.brand_id === brand.id);
            const effectiveStatus = overrides[brand.id] ?? pref?.status ?? "none";
            const isOn = effectiveStatus === "subscribed" || effectiveStatus === "pending";
            const isPending = !!pending[brand.id];

            let badge: { label: string; variant: "default" | "secondary" | "outline" | "destructive" } = {
              label: "Not subscribed",
              variant: "outline",
            };
            if (suppression) {
              const scopeLabel = suppression.scope === "global" ? "Suppressed (global)" : "Suppressed";
              badge = {
                label: `${scopeLabel} — ${SUPPRESSION_LABELS[suppression.reason] ?? suppression.reason}`,
                variant: "destructive",
              };
            } else if (effectiveStatus === "subscribed") {
              badge = { label: "Subscribed", variant: "default" };
            } else if (effectiveStatus === "unsubscribed") {
              badge = { label: "Unsubscribed", variant: "secondary" };
            } else if (effectiveStatus === "pending") {
              badge = { label: "Pending", variant: "secondary" };
            }

            const row = (
              <div className="flex items-center gap-3 px-4 py-2.5">
                {brand.logo_url ? (
                  <img
                    src={brand.logo_url}
                    alt=""
                    className="h-7 w-7 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-7 w-7 rounded bg-muted flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{brand.name}</p>
                  <Badge variant={badge.variant} className="mt-0.5 text-[10px] font-normal">
                    {suppression && <ShieldAlert className="h-3 w-3 mr-1" />}
                    {badge.label}
                  </Badge>
                </div>
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Switch
                    checked={isOn}
                    disabled={!!suppression || isPending}
                    onCheckedChange={(v) => handleToggle(brand.id, v)}
                  />
                )}
              </div>
            );

            return suppression ? (
              <TooltipProvider key={brand.id} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>{row}</div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Suppressions can only be lifted from the Subscribers tab.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div key={brand.id}>{row}</div>
            );
          })
        )}
      </div>
    </div>
  );
};